import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { AccountStage, AccountStageOrder } from "../../constants/accountStages";
import CustomerAccountService, {
  AccountSourceApi,
  accountSourceToApiValue,
  apiSourceToUiValue,
  formatJoinedDate,
} from "../../services/CustomerAccountService";
import LeadService from "../../services/LeadService";

const SOURCE_OPTIONS = [
  { value: AccountSourceApi.WEBINAR, label: "Webinar" },
  { value: AccountSourceApi.QUALIFIED_LEAD, label: "Qualified Lead" },
  { value: AccountSourceApi.DIRECT_OUTREACH, label: "Direct Outreach" },
  { value: AccountSourceApi.REFERRAL, label: "Referral" },
  { value: AccountSourceApi.COLD_CALL, label: "Cold Call" },
  { value: AccountSourceApi.SOCIAL_MEDIA, label: "Social Media" },
];

const STAGE_OPTIONS = AccountStageOrder.map((stage) => ({
  value: stage,
  label:
    stage === AccountStage.NEW_ACCOUNT
      ? "New"
      : stage === AccountStage.ACTIVE_CLIENT
        ? "Active Client"
        : "Churned",
}));

const emptyForm = () => ({
  accountName: "",
  primaryContact: "",
  companyEmail: "",
  phone: "",
  notes: "",
  source: AccountSourceApi.WEBINAR,
  stage: AccountStage.NEW_ACCOUNT,
  initialValue: "",
  ConvertedFromLead: "",
});

const fieldStyle = {
  backgroundColor: "#f4f5f7",
  border: "1px solid #dee2e6",
  borderRadius: 6,
  fontFamily: "'Inria Serif', Georgia, serif",
  fontSize: "0.9rem",
};

const labelStyle = {
  fontFamily: "'Inria Serif', Georgia, serif",
  fontSize: "0.85rem",
  marginBottom: 4,
};

function parseInitialValue(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatCashInflow(amount) {
  if (!amount) return "—";
  return `₹${amount.toLocaleString("en-IN")}`;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email ?? "").trim());
}

function normalizePhoneDigits(phone) {
  return String(phone ?? "").replace(/\D/g, "");
}

function isValidPhone(phone) {
  const length = normalizePhoneDigits(phone).length;
  return length >= 10 && length <= 12;
}

const AddAccountModal = ({ show, onHide, onCreate, onUpdate, account }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [leads, setLeads] = useState([]);
  const [showLeadList, setShowLeadList] = useState(false);

  useEffect(() => {
    if (!show) return;
    const initialValues = account
      ? {
          accountName: account.company || "",
          primaryContact: account.contactName || "",
          companyEmail:
            account.email && account.email !== "—" ? account.email : "",
          phone:
            account.phone && account.phone !== "—"
              ? normalizePhoneDigits(account.phone)
              : "",
          notes: account.notes || "",
          source:
            account.source && account.source !== "—"
              ? accountSourceToApiValue(account.source)
              : AccountSourceApi.WEBINAR,
          stage: account.stage || AccountStage.NEW_ACCOUNT,
          initialValue:
            account.revenue && account.revenue !== "—"
              ? String(account.revenue)
              : "",
          ConvertedFromLead:
            account.ConvertedFromLead && account.ConvertedFromLead !== "—"
              ? account.ConvertedFromLead
              : "",
        }
      : emptyForm();

    setForm(initialValues);
    setErrors({});
    setSubmitError("");
    setCreateLoading(false);
    setShowLeadList(false);

    (async () => {
      try {
        const res = await LeadService.getLeads();
        const list = res?.data?.data?.leadDetails ?? res?.data?.leadDetails ?? [];
        setLeads(
          (Array.isArray(list) ? list : []).map((l) => ({
            id: String(l.leadId ?? l.id ?? ""),
            name: l.name ?? `${l.firstName || ""} ${l.lastName || ""}`.trim(),
            company: l.company ?? "",
          })),
        );
      } catch {
        setLeads([]);
      }
    })();
  }, [show, account]);

  const filteredLeads = useMemo(() => {
    const q = form.ConvertedFromLead.trim().toLowerCase();
    if (!q) return leads.slice(0, 8);
    return leads
      .filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.company.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [leads, form.ConvertedFromLead]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    const isCreate = !account;
    const email = form.companyEmail.trim();
    const phone = form.phone.trim();

    if (!form.accountName.trim()) next.accountName = "Account name is required";
    if (!form.primaryContact.trim()) next.primaryContact = "Primary contact is required";

    if (isCreate) {
      if (!email) next.companyEmail = "Email is required";
      else if (!isValidEmail(email)) next.companyEmail = "Enter a valid email";

      if (!phone) next.phone = "Phone number is required";
      else if (!isValidPhone(phone)) {
        next.phone = "Enter a valid 10-digit phone number";
      }
    } else {
      if (email && !isValidEmail(email)) next.companyEmail = "Enter a valid email";
      if (phone && !isValidPhone(phone)) {
        next.phone = "Enter a valid 10-digit phone number";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const isChurnedEdit = Boolean(account) && account.stage === AccountStage.CHURNED;

  const handleSubmit = async () => {
    if (isChurnedEdit) return;
    if (!validate()) return;
    setSubmitError("");
    setCreateLoading(true);

    const amount = parseInitialValue(form.initialValue);
    const payload = {
      accountName: form.accountName.trim(),
      primaryContact: form.primaryContact.trim(),
      companyEmail: form.companyEmail.trim(),
      phoneNumber: normalizePhoneDigits(form.phone),
      source: accountSourceToApiValue(form.source),
      stage: form.stage,
      initialValue: amount,
      convertedFromLead: form.ConvertedFromLead.trim(),
      notes: form.notes.trim(),
      isDeleted: false,
    };

    try {
      const importPayload = {
        ...payload,
        id: account?.accountId,
      };
      const resp = account
        ? await CustomerAccountService.updateCustomerAccount(importPayload)
        : await CustomerAccountService.createCustomerAccount(payload);
      const success = resp?.data?.success ?? false;
      if (!success) {
        throw new Error(resp?.data?.message || "Failed to save account.");
      }

      const joined = formatJoinedDate(new Date());

      const updatedAccount = {
        id:resp?.data?.data?.id || resp?.data?.data?.accountId || account?.accountId || `local-${Date.now()}`,
        company: form.accountName.trim(),
        contactName: form.primaryContact.trim(),
        email: form.companyEmail.trim() || "—",
        phone: normalizePhoneDigits(form.phone) || "—",
        source: apiSourceToUiValue(form.source),
        stage: form.stage,
        cashInflow: formatCashInflow(amount),
        revenue: amount ? String(amount) : "—",
        joined,
        ConvertedFromLead: form.ConvertedFromLead.trim(),
        linkedLead: form.ConvertedFromLead.trim() || "—",
      };

      if (account) {
        onUpdate?.(updatedAccount);
      } else {
        onCreate?.(updatedAccount);
      }
      onHide?.();
    } catch (err) {
      console.error(err);
      setSubmitError(err?.response?.data?.message || err?.message || "Failed to save account.");
    } finally {
      setCreateLoading(false);
    }
  };

  const selectLead = (lead) => {
    if (isChurnedEdit) return;
    update("ConvertedFromLead", lead.name || "Unnamed lead");
    setShowLeadList(false);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      contentClassName="border-0"
      style={{ fontFamily: "'Inria Serif', Georgia, serif" }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title style={{ fontFamily: "'Inria Serif', Georgia, serif", fontWeight: 700 }}>
          {account ? "Edit Account" : "Add Account"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2" style={{ maxHeight: "70vh" }}>
        <div
          className="text-muted mb-3"
          style={{ fontSize: "0.7rem", letterSpacing: "0.1em", fontWeight: 600 }}
        >
          ACCOUNT DETAILS
        </div>

        {isChurnedEdit ? (
          <div className="alert alert-secondary small py-2 mb-3">
            This account is churned. Details cannot be edited.
          </div>
        ) : null}

        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Account Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={form.accountName}
              onChange={(e) => update("accountName", e.target.value)}
              placeholder="Urban Realty Developers"
              style={fieldStyle}
              isInvalid={Boolean(errors.accountName)}
              disabled={isChurnedEdit}
            />
            <Form.Control.Feedback type="invalid">{errors.accountName}</Form.Control.Feedback>
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>
                  Primary Contact <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={form.primaryContact}
                  onChange={(e) => update("primaryContact", e.target.value)}
                  placeholder="Pooja Singh"
                  style={fieldStyle}
                  isInvalid={Boolean(errors.primaryContact)}
                  disabled={isChurnedEdit}
                />
                <Form.Control.Feedback type="invalid">{errors.primaryContact}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>
                  Company Email {!account ? <span className="text-danger">*</span> : null}
                </Form.Label>
                <Form.Control
                  type="email"
                  value={form.companyEmail}
                  onChange={(e) => update("companyEmail", e.target.value)}
                  placeholder="pooja@urbanrealty.com"
                  style={fieldStyle}
                  isInvalid={Boolean(errors.companyEmail)}
                  disabled={Boolean(account) || isChurnedEdit}
                />
                <Form.Control.Feedback type="invalid">{errors.companyEmail}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>
                  Phone {!account ? <span className="text-danger">*</span> : null}
                </Form.Label>
                <Form.Control
                  value={form.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    update("phone", digits);
                  }}
                  placeholder="9876543210"
                  inputMode="numeric"
                  style={fieldStyle}
                  isInvalid={Boolean(errors.phone)}
                  disabled={isChurnedEdit}
                />
                <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>Source</Form.Label>
                <Form.Select
                  value={form.source}
                  onChange={(e) => update("source", Number(e.target.value))}
                  style={fieldStyle}
                  disabled={isChurnedEdit}
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>Stage</Form.Label>
                <Form.Select
                  value={form.stage}
                  onChange={(e) => update("stage", e.target.value)}
                  style={fieldStyle}
                  disabled={isChurnedEdit}
                >
                  {STAGE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>Initial Value</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ ...fieldStyle, borderRight: 0 }}>₹</InputGroup.Text>
                  <Form.Control
                    value={form.initialValue}
                    onChange={(e) => update("initialValue", e.target.value)}
                    placeholder="0"
                    style={{ ...fieldStyle, borderLeft: 0 }}
                    disabled={isChurnedEdit}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-1 position-relative">
            <Form.Label style={labelStyle}>Converted From Lead (optional)</Form.Label>
            <Form.Control
              value={form.ConvertedFromLead}
              onChange={(e) => {
                if (isChurnedEdit) return;
                update("ConvertedFromLead", e.target.value);
                setShowLeadList(true);
              }}
              onFocus={() => !isChurnedEdit && setShowLeadList(true)}
              placeholder="Search lead..."
              style={fieldStyle}
              disabled={isChurnedEdit}
            />
            {showLeadList && !isChurnedEdit && filteredLeads.length > 0 ? (
              <div
                className="position-absolute w-100 border rounded shadow-sm bg-white mt-1"
                style={{ zIndex: 10, maxHeight: 160, overflowY: "auto" }}
              >
                {filteredLeads.map((lead) => (
                  <button
                    key={lead.id}
                    type="button"
                    className="dropdown-item text-start py-2 px-3 border-0 bg-white w-100"
                    style={{ fontSize: "0.85rem" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectLead(lead)}
                  >
                    <div className="fw-semibold">{lead.name || "Unnamed lead"}</div>
                    {lead.company ? (
                      <div className="text-muted small">{lead.company}</div>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Notes
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Add any notes about this account here..."
              style={fieldStyle}
              isInvalid={Boolean(errors.notes)}
              disabled={isChurnedEdit}
            />
            <Form.Control.Feedback type="invalid">{errors.notes}</Form.Control.Feedback>
          </Form.Group>

          {submitError ? (
            <div className="text-danger small mb-3">{submitError}</div>
          ) : null}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="light"
          className="border px-3"
          style={{ fontFamily: "'Inria Serif', Georgia, serif" }}
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="px-3"
          style={{ fontFamily: "'Inria Serif', Georgia, serif" }}
          onClick={handleSubmit}
          disabled={createLoading || isChurnedEdit}
        >
          {createLoading
            ? account
              ? "Saving..."
              : "Creating..."
            : account
            ? "Save Changes"
            : "Create Account"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAccountModal;
